import {useCallback, useEffect, useState} from "react";
import Order from "@components/Order";
import NoLocationPermission from "@components/Location/NoLocationPermission";
import LocationOutsidePremisses from "@components/Location/LocationOutsidePresmisses";
import TableOrderService from "@services/TableOrderService";
import ReCaptchaWrapper from "@hoc/partials/RecaptchaWrapper";
import {createSearchParams, useNavigate} from "react-router-dom";
import {useParams} from "react-router";
import {useDispatch} from "react-redux";

const TableOrderPage = () => {

    const navigate = useNavigate();
    const {tableQr} = useParams();
    const dispatch = useDispatch();
    const [canAccess, setCanAccess] = useState('order');
    const [userLocation, setUserLocation] = useState({});
    const urlParams = new URLSearchParams(window.location.search);
    const businessId = parseInt(urlParams.get('businessId'));
    const [geolocationPermission, setGeolocationPermission] = useState('unknown');


    const [hasAccess, setHasAccess] = useState(true);
    const [data, setData] = useState({});
    const [table, setTable] = useState({});
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState({});
    const [supportedPaymentMethods, setSupportedPaymentMethods] = useState([]);
    const [qs, setQs] = useState('');
    const [viewModeOnly, setViewModeOnly] = useState(true);

    useEffect(() => {
      if (tableQr) {
        TableOrderService.getMenu(tableQr, qs)
          .then((res) => {
            navigate({
              search: `?${createSearchParams({
                businessId: res?.data?.id
              })}`
            });
            setHasAccess(true);
            setData(res.data);
            const {productCategories} = res.data;
            setCategories(productCategories);
            if (productCategories.length > 0) {
              setSelectedCategory(productCategories[0]);
            }
            setTable(res?.data?.tables[0])
            if (res.data.supportedPaymentMethods && res.data.supportedPaymentMethods.includes("POK")) {
              const pokIndex = res.data.supportedPaymentMethods.indexOf("POK");

              if (pokIndex > 0) {
                res.data.supportedPaymentMethods.splice(pokIndex, 1);
                res.data.supportedPaymentMethods.unshift("POK");
              }
            }

            setSupportedPaymentMethods(res.data.supportedPaymentMethods);
            const isViewMode = res.data?.services.some(item => item.serviceId === 2 && item.isActive === false);
            setViewModeOnly(isViewMode)
            if (isViewMode) {
              setCanAccess('order')
            }
          })
          .catch(() => {
            setHasAccess(false)
          })
      }
    }, [dispatch, navigate, tableQr, qs]);

    useEffect(() => {
      if (!viewModeOnly) {
        const handlePermissionChange = (event) => {
          setGeolocationPermission(event.target.state);
        };

        const permissionStatus = navigator.permissions.query({name: 'geolocation'});

        permissionStatus.then((status) => {
          setGeolocationPermission(status.state);

          status.addEventListener('change', handlePermissionChange);
        });

        return () => {
          permissionStatus.then((status) => {
            status.removeEventListener('change', handlePermissionChange);
          })
        }
      }
    }, [viewModeOnly]);

    const getUserLocation = useCallback(() => {
      if (!viewModeOnly) {
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const latitude = position.coords.latitude;
              const longitude = position.coords.longitude;
              setUserLocation({latitude, longitude});
              if (businessId) {
                const payload = {
                  businessId,
                  coordinates: {
                    lat: latitude,
                    lng: longitude
                  }
                }
                TableOrderService.verifyLocation(payload)
                  .then((res) => {
                    setCanAccess('order')
                  })
                  .catch(() => {
                    setCanAccess('notInPremisses')
                  });
              }
            },
            (error) => {
              setCanAccess('denied')
            }
          );
        } else {
          setCanAccess('denied')
        }
      }
    }, [businessId, viewModeOnly])

    useEffect(() => {
      if (!viewModeOnly) {
        getUserLocation();
      }
    }, [getUserLocation, geolocationPermission, viewModeOnly]);

    useEffect(() => {
      if (businessId && userLocation?.latitude && userLocation?.longitude) {
        const payload = {
          businessId,
          coordinates: {
            lat: userLocation?.latitude,
            lng: userLocation?.longitude
          }
        }
        TableOrderService.verifyLocation(payload)
          .then(() => {
            setCanAccess('order')
          })
          .catch(() => {
            setCanAccess('notInPremisses')
          });
      }
    }, [businessId, userLocation])

    return (
      <ReCaptchaWrapper>
        {
          canAccess === 'order' ?
            <Order userLocation={userLocation}
                   data={data} table={table}
                   canAccess={hasAccess}
                   categories={categories}
                   supportedPaymentMethods={supportedPaymentMethods}
                   onSearch={setQs}
                   selectedCategory={selectedCategory}
                   onCategorySelect={setSelectedCategory}
                   viewMode={viewModeOnly}/>
            : (canAccess === 'denied' ? <NoLocationPermission/> :
              <LocationOutsidePremisses/>)
        }
      </ReCaptchaWrapper>
    );
  }
;

export default TableOrderPage;
